import { Box, Stack, Typography } from "@mui/joy";
import { useState, useEffect, useMemo } from "react";
import CandidateCard from "components/common/CandidateCard";
import { useApplicants } from "context/ApplicantsContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CandidateListHeader,
  FilterModal,
  LoadingCandidates,
  NoCandidates
} from "./components";
import MeetCandidatesButton from "./components/MeetCandidatesButton";
import { useSession } from "context/SessionContext";

interface CandidateListProps {
  candidates: any[];
  activeVideoId: string;
  muted: boolean;
  setMuted: (muted: boolean) => void;
  playing: boolean;
  setPlaying: (playing: boolean) => void;
  onVisibilityChange: (videoId: string, isVisible: boolean) => void;
  loading?: boolean;
  error?: string | null;
  hasMore?: boolean;
  onLoadMore?: () => Promise<void>;
  totalCandidates?: number;
  enableZipcodeFilter?: boolean;
  enableRolesFilter?: boolean;
  enableEmployerFilter?: boolean;
  enableEmployerPositionsFilter?: boolean;
  customFilterHandler?: (filters: { zipcode: string; roles: string[]; employerPositions: string[] }) => void;
  customCurrentEmployerPositions?: string[];
  alternateHeader?: React.ReactNode;
  alternateCallToActionButton?: React.ReactNode;
  onRemoveApplicant?: (id: string) => void;
  onShowFilters?: () => void;
  showEmployerToggle?: boolean;
}

const CandidateList: React.FC<CandidateListProps> = ({ 
  candidates,
  activeVideoId,
  muted,
  setMuted,
  playing,
  setPlaying,
  onVisibilityChange = () => {},
  loading = false,
  error = null,
  hasMore = false,
  onLoadMore,
  totalCandidates = 0,
  enableZipcodeFilter = true,
  enableRolesFilter = true,
  enableEmployerFilter = true,
  enableEmployerPositionsFilter = false,
  customFilterHandler,
  customCurrentEmployerPositions,
  alternateHeader,
  alternateCallToActionButton,
  onRemoveApplicant,
  onShowFilters,
  showEmployerToggle = false
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentZipcode = searchParams.get('zipcode') || '';
  const currentGlobalPositions = searchParams.get('roles') || '';
  const employersParam = searchParams.get('employers') || '';
  const employers = useMemo(() => 
    employersParam ? employersParam.split(',').map(p => p.trim()).filter(Boolean) : [], 
    [employersParam]
  );
  const currentEmployerPositions = searchParams.get('employerPositions') || '';

  const { selectedApplicantIds, clearApplicantIds } = useApplicants();
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const hasActiveFilters = Boolean(currentZipcode || currentGlobalPositions || currentEmployerPositions);

  const handleMeetSelected = () => {
    const applicantIdsParam = selectedApplicantIds.join(',');
    navigate(`/hiring/candidates/selected?applicantIds=${applicantIdsParam}`);
  };

  // Handle scroll to bottom for infinite loading
  useEffect(() => {
    const handleScroll = () => {
      if (!hasMore || loading || !onLoadMore) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Check if user is near the bottom (within 300px - trigger earlier)
      if (scrollTop + windowHeight >= documentHeight - 300) {
        onLoadMore();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, loading, onLoadMore]);

  const handleFilterApply = (filters: { zipcode: string; roles: string[]; employers: string[]; employerPositions: string[] }) => {
    // Use custom filter handler if provided (e.g., for PositionListing page)
    if (customFilterHandler) {
      customFilterHandler(filters);
      return;
    }
    
    // Default behavior - update URL parameters
    const newSearchParams = new URLSearchParams();
    
    if (filters.zipcode && filters.zipcode.trim() !== '') {
      newSearchParams.set('zipcode', filters.zipcode);
    }
    if (filters.roles && filters.roles.length > 0) {
      newSearchParams.set('roles', filters.roles.join(','));
    }
    if (filters.employers && filters.employers.length > 0) {
      newSearchParams.set('employers', filters.employers.join(','));
    }
    if (filters.employerPositions && filters.employerPositions.length > 0) {
      newSearchParams.set('employerPositions', filters.employerPositions.join(','));
    }
    
    setSearchParams(newSearchParams);
  };
  
  const handleClearFilter = (filterType: 'zipcode' | 'roles' | 'employers' | 'employerPositions') => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(filterType);
    setSearchParams(newSearchParams);
  };

  const handleEmployerFilterChange = (employers: string[]) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (employers && employers.length > 0) {
      newSearchParams.set('employers', employers.join(','));
    } else {
      newSearchParams.delete('employers');
    }
    
    setSearchParams(newSearchParams);
  };

  // Show loading spinner during initial load
  if (loading && candidates.length === 0) {
    return <LoadingCandidates />;
  }

  return (
    <Box>
      { 
        alternateHeader || 
        <CandidateListHeader
          candidateCount={totalCandidates || candidates.length}
          selectedApplicantsCount={selectedApplicantIds.length}
          selectedApplicantIds={selectedApplicantIds}
          hasActiveFilters={hasActiveFilters}
          currentZipcode={currentZipcode}
          currentPositions={currentGlobalPositions}
          currentEmployerPositions={currentEmployerPositions}
          currentEmployers={employers}
          onClearFilter={handleClearFilter}
          onEmployerFilterChange={handleEmployerFilterChange}
          clearSelection={clearApplicantIds}
          setFilterModalOpen={onShowFilters ? onShowFilters : setFilterModalOpen}
          showEmployerToggle={showEmployerToggle}
        />
      }

      <Stack spacing={6} component="ul" sx={{ py: {xs: 4, sm: 0} }}>
        {candidates.length === 0 && !loading ? (
          <NoCandidates />
        ) : (
          <>
          {candidates.map((candidate) => (
            <CandidateCard 
              key={candidate.uuid} 
              candidate={candidate} 
              isActive={candidate.videoUuid === activeVideoId}
              muted={muted}
              setMuted={setMuted}
              playing={playing}
              setPlaying={setPlaying}
              onVisibilityChange={(isVisible: boolean) => onVisibilityChange(candidate.videoUuid, isVisible)}
              onRemoveApplicant={onRemoveApplicant}
            />
          ))}
          </>
        )}
      </Stack>
      

      { 
        alternateCallToActionButton || 
        <MeetCandidatesButton 
          onClick={handleMeetSelected}
          setFilterModalOpen={setFilterModalOpen}
        />
      } 

      {/* Loading spinner for infinite scroll */}
      {loading && <LoadingCandidates message="Loading more people..." />}

      {/* Error message */}
      {error && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography level="body-md" sx={{ color: '#d32f2f' }}>
            {error}
          </Typography>
        </Box>
      )}

      {/* <MeetCandidatesButton onClick={handleMeetSelected} /> */}

      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApply={handleFilterApply}
        currentZipcode={currentZipcode}
        currentGlobalPositions={currentGlobalPositions ? currentGlobalPositions.split(',') : []}
        currentEmployerPositions={customCurrentEmployerPositions || (currentEmployerPositions ? currentEmployerPositions.split(',') : [])}
        currentEmployerFilterValue={employers}
        enableZipcodeFilter={enableZipcodeFilter}
        enableRolesFilter={enableRolesFilter}
        enableEmployerFilter={enableEmployerFilter}
        enableEmployerPositionsFilter={enableEmployerPositionsFilter}
      />
    </Box>
  );
};

export default CandidateList; 